import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, type StreamViewer } from '../lib/api-client';

interface UseStreamViewersOptions {
  streamKey: string;
  isEnabled: boolean;
  pollIntervalMs?: number;
}

interface UseStreamViewersReturn {
  viewers: StreamViewer[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useStreamViewers({
  streamKey,
  isEnabled,
  pollIntervalMs = 2000,
}: UseStreamViewersOptions): UseStreamViewersReturn {
  const [viewers, setViewers] = useState<StreamViewer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchViewers = useCallback(async () => {
    const hasToken = Boolean(apiClient.getAccessToken());
    if (!isEnabled || !hasToken) {
      return;
    }
    // Only show loading indicator on initial load, not during background polling
    const isInitialLoad = isInitialLoadRef.current;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await apiClient.listStreamViewers(streamKey);
      if (isMountedRef.current) {
        setViewers(response.viewers);
        if (isInitialLoad) {
          isInitialLoadRef.current = false;
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        // 404エラー（ストリームが存在しない）は静かに処理
        // これはログアウト後や配信終了後に発生する正常な状態
        const is404 = err instanceof Error && err.message.includes('アクティブではありません');
        if (!is404) {
          console.error('[useStreamViewers] Failed to fetch viewers', err);
          const detail = err instanceof Error ? err.message : '視聴者リストの取得に失敗しました';
          setError(detail);
        } else {
          // 404の場合はviewersをクリアするだけ
          setViewers([]);
        }
      }
    } finally {
      if (isMountedRef.current && isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, [streamKey, isEnabled]);

  useEffect(() => {
    const hasToken = Boolean(apiClient.getAccessToken());
    if (!isEnabled || !hasToken) {
      setViewers([]);
      setError(null);
      return;
    }

    fetchViewers();

    if (pollIntervalMs <= 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      fetchViewers();
    }, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchViewers, isEnabled, pollIntervalMs]);

  return {
    viewers,
    isLoading,
    error,
    refresh: fetchViewers,
  };
}
